import { Button, Flex, Input, List, Popover, Space, Tag, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { Fragment, useEffect, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { IoArchiveOutline } from 'react-icons/io5';
import { useParams } from 'react-router-dom';
import { PriorityOptions, ProgressOptions, TASk_TABS } from '../../constants';
import { customDate } from '../../helper';
import useLabels from '../../hooks/useLabels';
import useRestaurants from '../../hooks/useRestaurants';
import useTasks from '../../hooks/useTasks';
import { getTasks, updateTaskProgress } from '../../redux/actions/tasks';
import { getHighlightedText } from '../../utils';
import ChecklistProgress from '../ChecklistProgress';
import DraggableTable from '../DraggableTable';
import DueDateBadge from '../DueDateBadge';
import DueDateFilter from '../DueDateFilter';
import TaskDetailView from '../TaskDetailView';
import UserAvatar from '../UserAvatar';
import UserGroup from '../UserGroup';

const CompletedTask = ({ restaurant }) => {
  const { id } = useParams();
  const {
    completed: { tasks, pagination },
  } = useTasks();
  const { users } = useRestaurants();
  const { labels: allLabels } = useLabels();

  // const [data, setData] = useState(taskList.filter((el) => el.progress === 'completed') ?? []);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [isTaskView, setIsTaskView] = useState(false);
  const [taskData, setTaskData] = useState();
  const [currentPage, setCurrentPage] = useState(1);


  // const handleOrderChange = (order) => {
  //   localStorage.setItem('completedTaskOrder', JSON.stringify(order));
  //   const completedTaskOrder = updateOrderById(data, order);
  //   setData(completedTaskOrder);
  // };

  const handleTableChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setCurrentPage(pagination.current);

    const { due_date, ...restFilters } = filters;
    const dateRange = due_date?.[0];
    const start_date = dateRange?.[0] ? dayjs(dateRange[0], "DD/MM/YYYY").format("YYYY-MM-DD") : undefined;
    const end_date = dateRange?.[1] ? dayjs(dateRange[1], "DD/MM/YYYY").format("YYYY-MM-DD") : undefined;

    getTasks({
      id,
      filter: TASk_TABS.COMPLETED,
      page: pagination.current,
      ...restFilters,
      start_date,
      end_date,
    });
  };

  const handleArchive = async (taskId) => {
    const res = await updateTaskProgress(taskId, { progress: 2 }, { toast: false });
    if (res.ack)
      getTasks({
        id,
        filter: TASk_TABS.COMPLETED,
        page: currentPage,
      });
  };

  const columns = [
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      // hidden: !isManager(),
      render: (_, record) => {
        return (
          <Tooltip title="Archived Task">
            <Button
              type="dashed"
              icon={<IoArchiveOutline />}
              ghost
              danger
              styles={{
                icon: {
                  fontSize: 12,
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleArchive(record?.id);
              }}
              size="small"
            />
          </Tooltip>
        );
      },
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      filteredValue: filteredInfo.title || null,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="task-header-search">
          <Input
            placeholder="Search title..."
            size="large"
            allowClear
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys([e.target.value?.trimStart()])}
            onPressEnter={() => confirm()}
            suffix={<FiSearch />}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} icon={<FiSearch />} size="small">
              Search
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                confirm();
              }}
              color="danger"
              variant="filled"
              size="small"
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <FiSearch style={{ color: filtered ? '#1677ff' : undefined }} />,
      render: (title, record) => {
        const searchText = filteredInfo?.title?.[0]?.toLowerCase();
        return (
          <Fragment>
            <Typography.Text strong ellipsis={{ tooltip: true }}>
              {getHighlightedText(title, searchText)}
            </Typography.Text>
          </Fragment>
        );
      },
    },
    {
      title: 'Assignee',
      dataIndex: 'assignees',
      key: 'assignees',
      width: 120,
      filteredValue: filteredInfo.assignees || null,
      filters: users?.map((user) => {
        return {
          text: (
            <Space align="center">
              <UserAvatar user={user} size="small" style={{ marginRight: 8 }} />
              {`${user.first_name} ${user.last_name ?? ''}`}
            </Space>
          ),
          value: user.id,
        };
      }),
      render: (assignees) => {
        return (
          <UserGroup users={assignees} maxCount={2} />
        );
      },
    },
    {
      title: 'Checklist',
      dataIndex: 'checklist',
      key: 'checklist',
      width: 120,
      render: (checklist) => {
        return <ChecklistProgress trackCheckList={checklist} />;
      },
    },
    {
      title: 'Progress',
      dataIndex: 'progress',
      key: 'progress',
      width: 150,
      render: (progress) => {
        const progressOption = ProgressOptions.find((el) => el.value === progress);
        if (progressOption) {
          return (
            <Flex align="center" gap={8}>
              {progressOption.icon}
              {progressOption.label}
            </Flex>
          );
        }
      },
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      filteredValue: filteredInfo.priority || null,
      filters: PriorityOptions.map((option) => ({
        text: option.label,
        value: option.value,
      })),
      render: (priority) => {
        const priorityOption = PriorityOptions.find((el) => el.value === priority);
        if (priorityOption) {
          return (
            <Flex align="center" gap={8}>
              {priorityOption.icon}
              {priorityOption.label}
            </Flex>
          );
        }
      },
    },
    {
      title: 'Due Date',
      dataIndex: 'due_date',
      key: 'due_date',
      width: 220,
      filteredValue: filteredInfo.due_date || null,
      filterDropdown: (props) => <DueDateFilter {...props} />,
      render: (date, record) => {
        return <DueDateBadge date={date} completedAt={record.completed_at} />;
      },
    },
    {
      title: 'Labels',
      dataIndex: 'labels',
      key: 'labels',
      width: 150,
      filteredValue: filteredInfo.labels || null,
      filters: allLabels?.map((label) => ({
        text: <Tag color={label.color}>{label.title}</Tag>,
        value: label.id,
      })),
      render: (labels) => {
        const visibleLabel = labels && labels[0];
        const otherLabels = labels?.slice(1);

        return (
          <>
            {visibleLabel && <Tag color={visibleLabel?.color}>{visibleLabel?.title}</Tag>}
            {!!otherLabels?.length && (
              <Popover
                title="Other Labels"
                trigger="hover"
                style={{ padding: 0 }}
                content={
                  <List
                    size="small"
                    dataSource={otherLabels}
                    renderItem={(label) => (
                      <List.Item style={{ padding: '4px 0' }} key={label?.label_id}>
                        <Tag style={{ width: '100%' }} color={label?.color}>
                          {label?.title}
                        </Tag>
                      </List.Item>
                    )}
                  />
                }
              >
                <Tag color="blue">+{otherLabels.length}</Tag>
              </Popover>
            )}
          </>
        );
      },
    },
    {
      title: 'Completed',
      key: 'completed',
      width: 200,
      render: (_, record) => {
        const { fullDate } = customDate(record.completed_at);
        return (
          <Flex vertical gap={0}>
            <Typography.Text strong style={{ fontSize: 13 }}>
              {record.completed_by_name}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {fullDate ?? '-'}
            </Typography.Text>
          </Flex>
        );
      },
    },
  ];

  useEffect(() => {
    // const order = localStorage.getItem('completedTaskOrder');
    // if (order) {
    //   const completedTaskOrder = updateOrderById(data, JSON.parse(order));
    //   setData(completedTaskOrder);
    // }
    getTasks({ id, filter: TASk_TABS.COMPLETED, page: currentPage });
  }, []);

  return (
    <div className="CompletedTask">
      <DraggableTable
        columns={columns}
        onRow={(record) => ({
          onClick: () => {
            setTaskData(record);
            setIsTaskView(true);
          },
        })}
        data={tasks?.map((task) => ({ ...task, key: task.id }))}
        onOrderChange={() => { }}
        onChange={handleTableChange}
        pagination={{
          total: pagination?.total,
          pageSize: pagination?.limit,
          current: pagination?.page,
          showSizeChanger: false,
          showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} Records`,
        }}
      />

      {isTaskView && (
        <TaskDetailView isView open={isTaskView} task={taskData} restaurant={restaurant} onClose={() => setIsTaskView(false)} />
      )}
    </div>
  );
};

export default CompletedTask;
